/**
 * BaseTableTabs – pill-style status/category filters for table views.
 * Renders .tw-tabs wrapper with .nv-tab.nav_status divs (data-tabs-status, fa-check icon).
 * Uses rebrand/themes/purple/_tabs.scss; matches legacy markup for change_status integration.
 */

import React, { useState, useEffect } from 'react'

import type { BaseTableFilterOption } from './BaseTableTabs'

export interface BaseTableFilterGroup {
    options: BaseTableFilterOption[]
    groupKey: string
    /** Pill label for the group filter (category) */
    groupLabel?: string
    /** Sent as status param when the group pill is clicked (e.g. OPEN, SAVED) */
    groupValue?: string
    /** Show child status pills for this group (default false when grouped; flat options default true) */
    showStatusPills?: boolean
    /** Start this group on a new row (e.g. employee CANCELLED) */
    newRow?: boolean
}

export interface BaseTableGroupTabsProps {
    /** Filter options (value + label; label shown in tab) */
    groups: BaseTableFilterGroup[]
    /** Currently selected value */
    value: string
    /** Called when user selects a filter (use from React) */
    onChange?: (value: string) => void
    /** Global function name for PHP/mount (e.g. 'page_table_load'); called as fn('', '', value) */
    onChangeCallback?: string
    /** DOM input id to update before calling onChangeCallback (e.g. 'table_tab_status') */
    filterInputId?: string
    /** Optional class name for the wrapper */
    className?: string
    /** Optional id for the wrapper (e.g. for aria-describedby) */
    id?: string

    orderAlphabetical?: boolean
}

// Alias so index.tsx can re-export as BaseTableFilters
export type BaseTableGroupFiltersProps = BaseTableGroupTabsProps

export function BaseTableGroupTabs({
    groups,
    value,
    onChange,
    onChangeCallback,
    filterInputId,
    className,
    orderAlphabetical = true,
    id,
}: BaseTableGroupTabsProps) {

    const [selected, setSelected] = useState(value)

    /** Flat options (no group chip) default on; grouped tabs default off unless set per group */
    const groupShowsPills = (group: BaseTableFilterGroup) => group.showStatusPills ?? !group.groupValue

    const sortedGroups = React.useMemo(() => {
        let sorted = groups
        if (orderAlphabetical) {
            sorted = sorted.map((group) => ({
                ...group,
                options: groupShowsPills(group)
                    ? [...group.options].sort((a, b) =>
                        a.label.localeCompare(b.label)
                    )
                    : group.options,
            }))
        }
        return sorted

    }, [groups, orderAlphabetical])

    useEffect(() => {
        setSelected(value)
    }, [value])

    const handleChange = (newValue: string) => {
        setSelected(newValue)
        // Sync the hidden DOM input so update_address_bar()/query_string() picks up the new value
        if (filterInputId) {
            const el = document.getElementById(filterInputId) as HTMLInputElement | null
            if (el) el.value = newValue
        }
        if (onChangeCallback && typeof (window as unknown as Record<string, (a: string, b: string, c: string) => void>)[onChangeCallback] === 'function') {
            (window as unknown as Record<string, (a: string, b: string, c: string) => void>)[onChangeCallback]('', '', newValue)
        } else if (onChange) {
            onChange(newValue)
        }
    }

    const wrapperClass = ['tw-tabs', className].filter(Boolean).join(' ')

    /** Group chip selected (open, saved, …) — not a single status pill */
    const isGroupFilterSelected = (groupValue: string) => {
        if (!groupValue) {
            return false
        }
        return selected.toLowerCase() === groupValue.toLowerCase()
    }

    const isGroupActive = (group: BaseTableFilterGroup) => {
        const gv = group.groupValue ?? ''
        if (!gv) {
            return false
        }
        if (isGroupFilterSelected(gv)) {
            return true
        }
        if (!groupShowsPills(group)) {
            return false
        }
        return group.options.some(
            (opt) => opt.value === selected && opt.value.toLowerCase() !== gv
        )
    }

    return (
        <div
            id={id}
            className={wrapperClass}
            role="tablist"
            aria-label="Filter by status"
        >
            {sortedGroups.map((group, groupIndex) => {
                const groupValue = group.groupValue ?? ''
                const groupLabel = group.groupLabel ?? group.groupKey
                const groupActive = isGroupActive(group)
                const pillsVisible = groupShowsPills(group)

                const groupKey = group.groupKey || group.groupValue || `group-${groupIndex}`

                return (
                    <React.Fragment key={groupKey}>

                        {group.newRow ? (
                            <div className="tab-group-break" aria-hidden="true" />
                        ) : null}
                        <div
                            className={[
                                'tab-group',
                                groupValue ? 'glass-card --subtle --no-blur' : '',
                            ]
                                .filter(Boolean)
                                .join(' ')}
                        >
                            {/* {pillsVisible ? (
                                <div>{groupValue}<br /></div>
                            ) : null} */}

                            <div className="tab-group-options">
                                {groupValue && groupLabel && (
                                    <div
                                        id={`tab_status_${groupValue}`}
                                        className={['nv-tab', 'nav_status', 'tab_status', 'tab_status_group', groupActive ? 'active' : '']
                                            .filter(Boolean)
                                            .join(' ')}
                                        data-tabs-status={groupValue}
                                        role="tab"
                                        aria-selected={groupActive}
                                        aria-label={groupLabel}
                                        onClick={() => handleChange(groupValue)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault()
                                                handleChange(groupValue)
                                            }
                                        }}
                                        tabIndex={0}
                                    >
                                        <i className="fa fa-check tab-check" aria-hidden />
                                        {groupLabel}
                                        <i className="fa fa-angle-right"></i>
                                    </div>
                                )}

                                {pillsVisible && group.options.map((opt) => {
                                    const isActive =
                                        selected === opt.value || isGroupFilterSelected(groupValue)
                                    const tabClass = ['nv-tab', 'nav_status', 'tab_status', isActive ? 'active' : '']
                                        .filter(Boolean)
                                        .join(' ')

                                    return (
                                        <div
                                            key={opt.value}
                                            id={`tab_status_${opt.value}`}
                                            className={tabClass}
                                            data-tabs-status={opt.value}
                                            role="tab"
                                            aria-selected={isActive}
                                            aria-label={opt.label}
                                            onClick={() => handleChange(opt.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault()
                                                    handleChange(opt.value)
                                                }
                                            }}
                                            tabIndex={0}
                                        >
                                            <i className="fa fa-check tab-check" aria-hidden />
                                            {opt.label}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </React.Fragment>
                )
            })}
        </div>
    )
}

// Re-export under the name used by index.tsx and PHP callers
export { BaseTableGroupTabs as BaseTableGroupFilters }
